// Claude: RFXResponses.tsx — accordion view of matched sellers for a discover requirement 01-Apr-2026
// Claude: shortlist flow added — one-way, private to buyer, auto-adds to sellers list, meeting prompt 09-04-2026
// Claude: not-shortlisted action added — irreversible buyer rejection + hide filter; republished gate added 18-04-2026
import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Badge,
  Button,
  Spinner,
  HStack,
  IconButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Checkbox,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { MdArrowBack, MdRefresh, MdAdd } from "react-icons/md";
import moment from "moment";
import { Modal } from "antd";
import { useNavigate } from "react-router-dom";
import { RfxAPI } from "../../Apis/rfxAPI";
import { BuyerAPI } from "../../Apis/buyerAPI";
import { CommonAPI } from "../../Apis/commonApi";
import { isNewMeetingAllowed } from "../../Utils/meetingUtils"; // Claude: shared util 10-04-2026
import SellerProfileDrawer from "./SellerProfileDrawer";
import CreateMeetModal from "../../Components/Modals/CreateMeetModal";
import ConfirmationModal from "../../Components/Modals/ConfirmationModal";
import RelevanceModalBuyer from "../../Components/Modals/RelevanceModalBuyer";

const RELEVANCE_COLOR: Record<string, string> = {
  Relevant: "#159300",
  Existing_Relationship: "#0E122F",
  Under_Research: "#FF5F12",
  Not_Relevant: "#9B9B9B",
};

interface RFXResponsesProps {
  rfx: any;
  onBack: () => void;
  buyerPhoneNumber?: string; // Claude: phone gate — passed from Discover 10-04-2026
}

const RFXResponses = ({ rfx, onBack, buyerPhoneNumber }: RFXResponsesProps) => {
  const [data, setData] = useState<{ isDone: boolean; sellers: any[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [drawerSellerId, setDrawerSellerId] = useState<string | null>(null);
  const [drawerCompanyName, setDrawerCompanyName] = useState("");
  // Claude: shortlisted seller ids — initialised from responseStatus on load 09-04-2026
  const [shortlistedIds, setShortlistedIds] = useState<Set<string>>(new Set());
  // Claude: not-shortlisted seller ids — initialised from responseStatus on load 18-04-2026
  const [notShortlistedIds, setNotShortlistedIds] = useState<Set<string>>(new Set());
  // Claude: confirm modal state 09-04-2026
  const [confirmSeller, setConfirmSeller] = useState<{ sellerId: string; responseId: string; companyName: string; userId: string } | null>(null);
  const [shortlisting, setShortlisting] = useState(false);
  // Claude: not-shortlist confirm modal state 18-04-2026
  const [confirmNotShortlistSeller, setConfirmNotShortlistSeller] = useState<{ sellerId: string; responseId: string; companyName: string } | null>(null);
  const [notShortlisting, setNotShortlisting] = useState(false);
  // Claude: hide not-shortlisted filter 18-04-2026
  const [hideNotShortlisted, setHideNotShortlisted] = useState(false);
  // Claude: buyer pending response limit — tracks count across all open RFXs 18-04-2026
  const [pendingResponsesCount, setPendingResponsesCount] = useState(0);
  const [pendingResponseLimit, setPendingResponseLimit] = useState(3);
  // Claude: meeting prompt + create meet modal state — opened after shortlist confirmed 10-04-2026
  const [meetingPromptSeller, setMeetingPromptSeller] = useState<{ companyName: string; userId: string } | null>(null);
  const [meetModal, setMeetModal] = useState(false);
  // Claude: existing meeting check — same pattern as RFXDetail 10-04-2026
  const [meetingData, setMeetingData] = useState<any>({});
  const [meetConfirm, setMeetConfirm] = useState(false);
  const [currentMeet, setCurrentMeet] = useState("");
  // Claude: relevance modal state 24-04-2026
  const [releModal, setReleModal] = useState(false);
  const [relevanceData, setRelevanceData] = useState<any>({});
  const handleOpenRelevanceModal = () => setReleModal(true);
  const handleCloseRelevanceModal = () => setReleModal(false);

  const toast = useToast({ position: "top", containerStyle: { marginTop: "40vh" } });
  const navigate = useNavigate();


  const fetchMeetings = async () => {
    try {
      const meetRes = await CommonAPI.GetMeeting({ status: undefined });
      if (meetRes) {
        const meetings = JSON.parse(JSON.stringify(meetRes.data.data));
        const meetObj: any = {};
        meetings.forEach((element: any) => {
          meetObj[element.responderCompany] = {
            _id: element._id,
            requesterId: element.requesterId,
            responderId: element.responderId,
            requesterStatus: element.requesterStatus,
            responderStatus: element.responderStatus,
          };
        });
        setMeetingData(meetObj);
      }
    } catch {}
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await RfxAPI.getRFXMatchedSellers(rfx._id);
      const result = res?.data ?? res;
      setData(result);
      // Claude: initialise shortlisted + not-shortlisted sets from buyerResponseStatus on load 10-04-2026; not_shortlisted added 18-04-2026
      const ids = new Set<string>(
        (result?.sellers ?? [])
          .filter((s: any) => s.buyerResponseStatus === "shortlisted")
          .map((s: any) => String(s.seller?._id ?? s.sellerId))
      );
      setShortlistedIds(ids);
      const notIds = new Set<string>(
        (result?.sellers ?? [])
          .filter((s: any) => s.buyerResponseStatus === "not_shortlisted")
          .map((s: any) => String(s.seller?._id ?? s.sellerId))
      );
      setNotShortlistedIds(notIds);
      // Claude: populate pending limit state from API 18-04-2026
      setPendingResponsesCount(result?.pendingResponsesCount ?? 0);
      setPendingResponseLimit(result?.pendingResponseLimit ?? 3);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); fetchMeetings(); }, [rfx._id]);

  const isExpired = rfx.status === "expired";
  const isClosed = rfx.status === "closed";
  // Claude: republished added — buyer actions disabled on republished RFX 18-04-2026
  const isRepublished = rfx.status === "republished";
  const isInactive = isExpired || isClosed || isRepublished;
  const expiryDate = rfx.expiryDate ? moment(rfx.expiryDate).format("DD MMM YYYY") : null;
  const sellers: any[] = data?.sellers ?? [];

  const openDrawer = (sellerId: string, companyName: string) => {
    setDrawerSellerId(sellerId);
    setDrawerCompanyName(companyName);
  };

  // Claude: set up meeting from panel — only set meetingPromptSeller when opening CreateMeetModal 24-04-2026
  const handleSetupMeetingFromPanel = (companyName: string, userId: string) => {
    if (!buyerPhoneNumber) {
      toast({ description: "Please provide your contact number in the profile page", status: "error", duration: 3000, isClosable: false });
      return;
    }
    if (meetingData[companyName]) {
      const meetObj = meetingData[companyName];
      const status = meetObj.requesterId === userId ? meetObj.requesterStatus : meetObj.responderStatus;
      if (isNewMeetingAllowed(status)) {
        setMeetingPromptSeller({ companyName, userId });
        setMeetModal(true);
      } else {
        setCurrentMeet(meetObj._id);
        setMeetConfirm(true);
      }
    } else {
      setMeetingPromptSeller({ companyName, userId });
      setMeetModal(true);
    }
  };

  // Claude: confirm shortlist — call API, auto-add to sellers list, show meeting prompt 09-04-2026
  const handleConfirmShortlist = async () => {
    if (!confirmSeller) return;
    setShortlisting(true);
    try {
      await (RfxAPI as any).toggleShortlist(rfx._id, confirmSeller.responseId);
      await (BuyerAPI as any).addSellerToList({ sellerId: confirmSeller.sellerId });
      setShortlistedIds(prev => new Set(prev).add(confirmSeller.sellerId));
      setPendingResponsesCount(prev => Math.max(0, prev - 1)); // Claude: decrement pending count on shortlist 18-04-2026
      const { companyName, userId } = confirmSeller;
      setConfirmSeller(null);
      setMeetingPromptSeller({ companyName, userId });
    } catch {
      toast({ description: "Failed to shortlist. Please try again.", status: "error", duration: 2000, isClosable: false });
      setConfirmSeller(null);
    } finally {
      setShortlisting(false);
    }
  };

  // Claude: confirm not-shortlist — call API, update local state 18-04-2026
  const handleConfirmNotShortlist = async () => {
    if (!confirmNotShortlistSeller) return;
    setNotShortlisting(true);
    try {
      await (RfxAPI as any).markNotShortlisted(rfx._id, confirmNotShortlistSeller.responseId);
      setNotShortlistedIds(prev => new Set(prev).add(confirmNotShortlistSeller.sellerId));
      setPendingResponsesCount(prev => Math.max(0, prev - 1)); // Claude: decrement pending count on not-shortlist 18-04-2026
      setConfirmNotShortlistSeller(null);
      toast({ description: "Marked as Not Shortlisted", status: "info", duration: 2000, isClosable: false });
    } catch {
      toast({ description: "Failed. Please try again.", status: "error", duration: 2000, isClosable: false });
      setConfirmNotShortlistSeller(null);
    } finally {
      setNotShortlisting(false);
    }
  };

  return (
    <Box minH="100vh" bg="#E8F5E9" px={3} py={3}>
      {/* Page header */}
      <Flex alignItems="flex-start" gap={2} mb={2}>
        <IconButton
          aria-label="Back"
          icon={<MdArrowBack size={18} />}
          size="sm" variant="ghost" color="gray.600"
          flexShrink={0} mt={0.5}
          onClick={onBack}
        />
        <Box flex={1} minW={0}>
          <Text fontSize="sm" fontWeight={600} color="gray.700" noOfLines={2} lineHeight="1.4">
            {rfx.rfxDetails}
          </Text>
          <Flex alignItems="center" gap={2} mt={1} flexWrap="wrap">
            <Badge fontSize="9px" px={2} py={0.5} borderRadius="full"
              bg={isInactive ? "gray.100" : "#FFF3E0"}
              color={isInactive ? "gray.500" : "orange.600"}
              textTransform="none" fontWeight={500}>
              {isClosed ? "Closed" : isExpired ? "Expired" : expiryDate ? `Open till ${expiryDate}` : "Open"}
            </Badge>
          </Flex>
        </Box>
        <IconButton
          aria-label="Refresh"
          icon={<MdRefresh size={16} />}
          size="sm" variant="ghost" color="gray.400"
          flexShrink={0} isLoading={loading}
          onClick={load}
        />
      </Flex>

      {/* Claude: hide not-shortlisted filter toggle 18-04-2026 */}
      {!loading && sellers.length > 0 && (
        <Flex justifyContent="flex-end" mb={2}>
          <Checkbox
            size="sm" fontSize="11px" colorScheme="green"
            isChecked={hideNotShortlisted}
            onChange={(e) => setHideNotShortlisted(e.target.checked)}
          >
            <Text fontSize="11px" color="gray.500">Hide Not Shortlisted</Text>
          </Checkbox>
        </Flex>
      )}

      {loading ? (
        <Flex justifyContent="center" py={10}>
          <Spinner size="sm" color="#0E122F" />
        </Flex>
      ) : sellers.length === 0 ? (
        <Box textAlign="center" py={10}>
          <Text fontSize="sm" color="gray.400">No matched sellers yet — check back later.</Text>
        </Box>
      ) : (
        <>
        <Accordion allowMultiple defaultIndex={[]}>
          {sellers.filter((s: any) => {
            if (!hideNotShortlisted) return true;
            const sid = String(s.seller?._id ?? s.sellerId);
            return !notShortlistedIds.has(sid);
          }).map((seller: any) => {
            const relColor = RELEVANCE_COLOR[seller.relevanceStatus] ?? "#FF5F12";
            const assets: any[] = seller.matchedAssets ?? [];
            const showScore = seller.totalKeywords > 0;
            const sellerId = String(seller.seller?._id ?? seller.sellerId);
            const companyName = seller.seller?.companyName ?? "";
            const sellerUserId = seller.seller?.userId ?? "";
            const responseId = String(seller.responseId ?? "");
            const isShortlisted = shortlistedIds.has(sellerId);
            const isNotShortlisted = notShortlistedIds.has(sellerId);

            return (
              <AccordionItem key={sellerId} bg="white" border="1px solid #D2D2D2" borderRadius="12px" mb={3} overflow="hidden">
                <AccordionButton px={3} py={3} _hover={{ bg: "gray.50" }}>
                  <Flex flex={1} alignItems="center" gap={2} minW={0}>
                    <Flex alignItems="center" gap={2} minW={0} flex={1}>
                      <Text
                        fontSize="sm" fontWeight={700} noOfLines={1}
                        color="#1565C0" textDecoration="underline" cursor="pointer" flexShrink={0}
                        onClick={(e) => { e.stopPropagation(); openDrawer(sellerId, companyName); }}
                      >
                        {companyName}
                      </Text>
                      <Tooltip label={seller.relevanceStatus?.replace(/_/g, " ")} hasArrow placement="top" fontSize="xs" sx={{ cursor: "pointer" }}>
                        <Box w="14px" h="14px" borderRadius="2px" bg={relColor} flexShrink={0} cursor="pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRelevanceData({
                              prequalified_company_name: companyName,
                              prequalified_user_id: sellerUserId,
                              event_id: "",
                              status: seller.relevanceStatus ?? "",
                            });
                            handleOpenRelevanceModal();
                          }}
                        />
                      </Tooltip>
                      {seller.seller?.verificationStatus === "verified" && (
                        <Text fontSize="9px" color="#2E7D32" fontWeight={400} flexShrink={0}>Verified Profile</Text>
                      )}
                      {/* Claude: Shortlisted badge — visible in header without expanding 09-04-2026 */}
                      {isShortlisted && (
                        <Badge fontSize="9px" px={2} py={0.5} borderRadius="full"
                          bg="#E8F5E9" color="#2E7D32" textTransform="none" fontWeight={600} flexShrink={0}>
                          Shortlisted
                        </Badge>
                      )}
                      {/* Claude: Not Shortlisted badge 18-04-2026 */}
                      {isNotShortlisted && (
                        <Badge fontSize="9px" px={2} py={0.5} borderRadius="full"
                          bg="#FFEBEE" color="#C62828" textTransform="none" fontWeight={600} flexShrink={0}>
                          Not Shortlisted
                        </Badge>
                      )}
                    </Flex>
                    {showScore && (
                      <Text fontSize="9px" color="gray.500" flexShrink={0} noOfLines={1}>
                        {seller.keywordMatchCount}/{seller.totalKeywords} keywords
                      </Text>
                    )}
                  </Flex>
                  <AccordionIcon color="gray.400" ml={2} />
                </AccordionButton>

                <AccordionPanel px={3} pb={3} pt={1}>
                  {seller.keywordsMatched && assets.length > 0 && (
                    <Box pl={0} pr={0} pb={2} overflowX="auto"
                      sx={{ scrollSnapType: "x mandatory", "&::-webkit-scrollbar": { display: "none" } }}>
                      <Flex gap={2} w="max-content" pr={2}>
                        {assets.map((asset: any) => (
                          <Box key={asset._id} w="150px" flexShrink={0} sx={{ scrollSnapAlign: "start" }} position="relative">
                            <Box w="150px" h="90px" borderRadius="10px" bg="gray.50"
                              border="1px solid" borderColor="gray.200"
                              cursor="pointer" px={3} py={2}
                              display="flex" flexDirection="column" justifyContent="space-between"
                              onClick={() => window.open(asset.assetUrl, "_blank")}
                              _hover={{ bg: "#F1F8E9", borderColor: "#A5D6A7" }}>
                              <Text fontSize="11px" fontWeight={600} color="#0E122F" noOfLines={3} lineHeight="1.4">
                                {asset.assetTitle}
                              </Text>
                              {asset.assetType && (
                                <Text fontSize="9px" color="#2E7D32" fontWeight={500} noOfLines={1}>
                                  {asset.assetType}
                                </Text>
                              )}
                            </Box>
                            <IconButton
                              aria-label="Add to feed"
                              icon={<MdAdd size={12} />}
                              size="xs" position="absolute" top="4px" right="4px"
                              borderRadius="full" bg="white" border="1px solid"
                              borderColor="gray.300" color="gray.500"
                              _hover={{ bg: "#E8F5E9", borderColor: "#A5D6A7", color: "#2E7D32" }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await (BuyerAPI as any).addToFeeds(asset._id);
                                  toast({ description: "Added to Saved Feeds", status: "success", duration: 2000, isClosable: false });
                                } catch {
                                  toast({ description: "Failed to add. Please try again.", status: "error", duration: 2000, isClosable: false });
                                }
                              }}
                            />
                          </Box>
                        ))}
                      </Flex>
                    </Box>
                  )}

                  <HStack mt={seller.keywordsMatched && assets.length > 0 ? 2 : 0} mb={2} flexWrap="wrap" gap={2}>
                    <Button
                      size="xs" variant="outline" fontSize="11px" borderRadius="full"
                      borderColor="#1565C0" color="#1565C0"
                      _hover={{ bg: "#E3F2FD" }}
                      onClick={() => openDrawer(sellerId, companyName)}
                    >
                      View All Assets
                    </Button>
                    {/* Claude: Set up a Meeting button — shown only once shortlisted 24-04-2026 */}
                    {isShortlisted && (
                      <Button
                        size="xs" variant="outline" fontSize="11px" borderRadius="full"
                        borderColor="#0E122F" color="#0E122F"
                        _hover={{ bg: "gray.100" }}
                        onClick={() => handleSetupMeetingFromPanel(companyName, sellerUserId)}
                      >
                        Set up a Meeting
                      </Button>
                    )}
                    {/* Claude: Add to Sellers List button removed — seller is added automatically on shortlist 10-04-2026 */}
                    {/* Claude: Shortlist + Not Shortlist buttons — hidden once either action taken or RFX inactive 09-04-2026; not_shortlisted + republished gate added 18-04-2026 */}
                    {!isShortlisted && !isNotShortlisted && !isInactive && responseId && (
                      <>
                        <Button
                          size="xs" fontSize="11px" borderRadius="full"
                          bg="#2E7D32" color="white"
                          _hover={{ bg: "#1B5E20" }}
                          onClick={() => setConfirmSeller({ sellerId, responseId, companyName, userId: sellerUserId })}
                        >
                          Shortlist
                        </Button>
                        <Button
                          size="xs" fontSize="11px" borderRadius="full"
                          variant="outline" borderColor="#C62828" color="#C62828"
                          _hover={{ bg: "#FFEBEE" }}
                          onClick={() => setConfirmNotShortlistSeller({ sellerId, responseId, companyName })}
                        >
                          Do Not Shortlist
                        </Button>
                      </>
                    )}
                  </HStack>

                  {seller.rfxResponseDetails && (
                    <Box mt={1}>
                      <Text fontSize="11px" color="gray.500" fontStyle="italic" noOfLines={4}>
                        "{seller.rfxResponseDetails}"
                      </Text>
                    </Box>
                  )}
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
        {/* Claude: pending response limit hint — shown when buyer has too many unacted responses across open RFXs 18-04-2026 */}
        {pendingResponsesCount >= pendingResponseLimit && (
          <Text fontSize="11px" color="orange.600" textAlign="center" mt={3} px={2}>
            Kindly mark seller's responses to your queries as either shortlisted / not shortlisted to continue receiving responses from more sellers.
          </Text>
        )}
        </>
      )}

      {/* Claude: seller profile drawer 01-Apr-2026 */}
      <SellerProfileDrawer
        sellerId={drawerSellerId}
        companyName={drawerCompanyName}
        isOpen={!!drawerSellerId}
        onClose={() => setDrawerSellerId(null)}
      />

      {/* Claude: shortlist confirmation modal — private, not reversible, auto-adds to sellers list 09-04-2026 */}
      <Modal open={!!confirmSeller} footer={null} closable={false} centered>
        <Box py={2}>
          <Text fontSize="sm" fontWeight={600} color="#0E122F" mb={3}>
            Shortlist {confirmSeller?.companyName}?
          </Text>
          <Text fontSize="xs" color="gray.500" mb={5}>
            The seller will not be notified. They will be automatically added to your Sellers List. This action cannot be undone.
          </Text>
          <Flex gap={3}>
            <Button flex={1} size="sm" bg="#2E7D32" color="white" _hover={{ bg: "#1B5E20" }}
              isLoading={shortlisting} onClick={handleConfirmShortlist}>
              Shortlist
            </Button>
            <Button flex={1} size="sm" variant="outline" color="gray.600"
              isDisabled={shortlisting} onClick={() => setConfirmSeller(null)}>
              Cancel
            </Button>
          </Flex>
        </Box>
      </Modal>

      {/* Claude: not-shortlist confirmation modal — irreversible, seller not notified 18-04-2026 */}
      <Modal open={!!confirmNotShortlistSeller} footer={null} closable={false} centered>
        <Box py={2}>
          <Text fontSize="sm" fontWeight={600} color="#0E122F" mb={3}>
            Mark {confirmNotShortlistSeller?.companyName} as Not Shortlisted?
          </Text>
          <Text fontSize="xs" color="gray.500" mb={5}>
            The seller will not be notified. This action cannot be undone.
          </Text>
          <Flex gap={3}>
            <Button flex={1} size="sm" bg="#C62828" color="white" _hover={{ bg: "#B71C1C" }}
              isLoading={notShortlisting} onClick={handleConfirmNotShortlist}>
              Confirm
            </Button>
            <Button flex={1} size="sm" variant="outline" color="gray.600"
              isDisabled={notShortlisting} onClick={() => setConfirmNotShortlistSeller(null)}>
              Cancel
            </Button>
          </Flex>
        </Box>
      </Modal>

      {/* Claude: meeting prompt modal — shown after shortlist confirmed; Yes opens CreateMeetModal 10-04-2026 */}
      <Modal open={!!meetingPromptSeller && !meetModal} footer={null} closable={false} centered>
        <Box py={2}>
          <Text fontSize="sm" color="gray.600" mb={3} lineHeight="1.6">
            The next step is to have the seller pitch their solution to you. An intro meeting would help in qualifying the seller. Would you like to set up a meeting?
          </Text>
          <Flex gap={3}>
            <Button flex={1} size="sm" bg="#0E122F" color="white" _hover={{ bg: "#1a2050" }}
              onClick={() => {
                // Claude: phone gate — mirrors SellerCard.tsx check 10-04-2026
                if (!buyerPhoneNumber) {
                  toast({ description: "Please provide your contact number in the profile page", status: "error", duration: 3000, isClosable: false });
                  return;
                }
                const companyName = meetingPromptSeller?.companyName ?? "";
                if (meetingData[companyName]) {
                  const meetObj = meetingData[companyName];
                  const userId = meetingPromptSeller?.userId ?? "";
                  const status = meetObj.requesterId === userId ? meetObj.requesterStatus : meetObj.responderStatus;
                  if (isNewMeetingAllowed(status)) {
                    setMeetModal(true);
                  } else {
                    setCurrentMeet(meetObj._id);
                    setMeetingPromptSeller(null);
                    setMeetConfirm(true);
                  }
                } else {
                  setMeetModal(true);
                }
              }}>
              Yes
            </Button>
            <Button flex={1} size="sm" variant="outline" color="gray.600"
              onClick={() => setMeetingPromptSeller(null)}>
              No
            </Button>
          </Flex>
        </Box>
      </Modal>

      {/* Claude: create meeting modal — opened from meeting prompt after shortlist 10-04-2026 */}
      <Modal open={meetModal} footer={null} closable={false} centered>
        <CreateMeetModal
          responderId={meetingPromptSeller?.userId}
          company={meetingPromptSeller?.companyName}
          context="RFX"
          handleCloseModal={() => { setMeetModal(false); setMeetingPromptSeller(null); fetchMeetings(); }}
        />
      </Modal>

      {/* Claude: open existing meeting confirmation — same pattern as RFXDetail 10-04-2026 */}
      <Modal open={meetConfirm} footer={null} closable={false} centered>
        <ConfirmationModal
          handleCloseModal={() => setMeetConfirm(false)}
          handleSubmit={() => { setMeetConfirm(false); navigate("/buyer/calendar?id=" + currentMeet); }}
          title="Open meeting already exists"
          message="Please view & edit the same. View details now?"
          confirmBtnText="Yes, View"
          rejectButtonText="No"
        />
      </Modal>
      {/* Claude: relevance modal — buyer sets relevance for a seller 24-04-2026 */}
      <Modal closeIcon={null} footer={null} title={null} open={releModal} onOk={handleCloseRelevanceModal} onCancel={handleCloseRelevanceModal} destroyOnClose>
        <RelevanceModalBuyer data={relevanceData} relaodData={load} handleCloseModal={handleCloseRelevanceModal} />
      </Modal>
    </Box>
  );
};

export default RFXResponses;
